import { useEffect, useState } from "react";

export function fmtUsd(n: number | undefined | null): string {
  if (n == null) return "—";
  if (n >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1) return `$${n.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
  return `$${n.toLocaleString("en-US", { maximumSignificantDigits: 4 })}`;
}

export function fmtNum(n: number | undefined | null): string {
  if (n == null) return "—";
  return n.toLocaleString("en-US");
}

export function fmtRating(n: number | null | undefined): string {
  return n == null ? "—" : n.toFixed(1);
}

export function timeAgo(iso: string, now: number = Date.now()): string {
  const d = now - Date.parse(iso);
  const m = Math.floor(d / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  const mo = Math.floor(days / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

export function shortAddr(a: string | null | undefined): string {
  if (!a) return "—";
  return a.length > 12 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a;
}

export function yearsSince(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return "—";
  const yrs = (now - Date.parse(iso)) / (365.25 * 864e5);
  if (yrs < 1) return "<1 yr";
  return `${Math.floor(yrs)} yr${Math.floor(yrs) === 1 ? "" : "s"}`;
}

/**
 * Hydration-safe relative time. The server (and the first client render) emit a
 * stable absolute date; after mount we swap in the relative string. Both renders
 * agree, so React never discards the server HTML — and crawlers see a real date.
 */
/**
 * A date rendered identically on the server and in the browser.
 *
 * toLocaleDateString() reads the runtime's locale and time zone, which differ
 * between the Worker and the visitor, so it cannot be used in server-rendered
 * markup without causing a hydration mismatch.
 */
export function isoDay(iso: string): string {
  return new Date(iso).toISOString().slice(0, 10);
}

/** Same idea, to the minute, and explicit that it is UTC. */
export function utcMinute(iso: string): string {
  return `${new Date(iso).toISOString().slice(0, 16).replace("T", " ")} UTC`;
}

export function TimeAgo({ iso }: { iso: string }) {
  const absolute = new Date(iso).toISOString().slice(0, 10);
  const [label, setLabel] = useState(absolute);
  useEffect(() => setLabel(timeAgo(iso)), [iso]);
  return (
    <time dateTime={iso} title={absolute}>
      {label}
    </time>
  );
}

/**
 * Minor units to a display string. The locale is pinned rather than taken from
 * the runtime: these strings are prices, and a number that renders differently
 * on the server and in the browser is a hydration mismatch waiting to happen.
 */
export function fmtMoney(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2,
    }).format(cents / 100);
  } catch {
    // An unknown currency code must not take the page down with it.
    return `${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}
